import React from "react";
import { useState } from "react";
import { Form, Button } from "react-bootstrap";
import _ from "lodash";
import JiraTicket from "../JiraTicket";
import { Spinner } from "react-bootstrap";

export default function ImportNdcsForm(props) {
  const [manufacturer, setManufacturer] = useState(null);
  const [inputFile, setInputFile] = useState(null);
  // Submit is disabled by default, until verification is completed
  const [fileValidated, setFileValidated] = useState(false);
  const [jiraTicket, setJiraTicket] = useState("");
  const [errorMessages, setErrorMessages] = useState([]);
  const [errorCount, setErrorCount] = useState(0);
  const [fileResults, setFileResults] = useState({});
  const [isBusy, setIsBusy] = useState(false);
  const [busyAction, setBusyAction] = useState(null);

  function fileIsXLSX(selectedFile) {
    if (selectedFile && selectedFile.files.length) {
      const extName = selectedFile.files[0].name.split(".").pop();
      if (extName != "xlsx") {
        alert("File must have extension .xlsx");
        selectedFile.value = null;
      }
      setInputFile(selectedFile.files[0]);
    }
  }

  function validateDisabled() {
    return !(manufacturer && inputFile && !isBusy);
  }

  function submitDisabled() {
    return !(props.can_upload && fileValidated && jiraTicket !== "" && !isBusy);
  }

  // Only show first three error messages
  const renderErrors = () =>
    _.map(errorMessages.slice(0, 3), (errorMessage, index) => (
      <li key={`error${index}`}>
        <i className="fa fa-circle-xmark" />
        <span className="p-3">{errorMessage}</span>
      </li>
    ));

  const renderNdcStatusMessage = () => {
    return fileResults ? (
      <>
        <span>{fileResults.ndc_overview}, </span>
        <span className="text-success">{fileResults.created_ndc_results}, </span>
        <span className="text-warning">{fileResults.updated_ndc_results}, </span>
        <span className="text-danger">{fileResults.deleted_ndc_results}</span>
        <ul>
          {_.map(fileResults.created_ndcs, (created_ndc, index) => (
            <li className="text-success" key={`created-${index}`}>
              <i className="fa fa-plus pe-2" />
              {created_ndc}
            </li>
          ))}
          {_.map(fileResults.updated_ndcs, (updated_ndc, index) => (
            <li className="text-warning" key={`updated-${index}`}>
              <i className="fa fa-arrows-left-right pe-2" />
              {updated_ndc}
            </li>
          ))}
          {_.map(fileResults.deleted_ndcs, (deleted_ndc, index) => (
            <li className="text-danger" key={`deleted-${index}`}>
              <i className="fa fa-minus pe-2" />
              {deleted_ndc}
            </li>
          ))}
        </ul>
      </>
    ) : null;
  };

  const renderNcpdpHcpcsUnitConversionStatusMessage = () => {
    return fileResults ? (
      <>
        <span>{fileResults.ncpdp_hcpcs_unit_conversion_overview}, </span>
        <span className="text-success">
          {fileResults.created_ncpdp_hcpcs_unit_conversion_results},{" "}
        </span>
        <span className="text-danger">
          {fileResults.deleted_ncpdp_hcpcs_unit_conversion_results}
        </span>
        <ul>
          {_.map(
            fileResults.created_ncpdp_hcpcs_unit_conversions,
            (created_ncpdp_hcpcs_unit_conversion, index) => (
              <li className="text-success" key={`created-ncpdp-${index}`}>
                <i className="fa fa-plus pe-2" />
                {created_ncpdp_hcpcs_unit_conversion}
              </li>
            )
          )}
          {_.map(
            fileResults.deleted_ncpdp_hcpcs_unit_conversions,
            (deleted_ncpdp_hcpcs_unit_conversion, index) => (
              <li className="text-danger" key={`deleted-ncpdp-${index}`}>
                <i className="fa fa-minus pe-2" />
                {deleted_ncpdp_hcpcs_unit_conversion}
              </li>
            )
          )}
        </ul>
      </>
    ) : null;
  };

  async function handleVerify(event) {
    event.preventDefault();

    if (!inputFile || isBusy) return;

    setIsBusy(true);
    setBusyAction("verify");

    const url = props.validate_url;
    const formData = new FormData();
    formData.append("file", inputFile);
    formData.append("manufacturer", manufacturer);
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').getAttribute("content"),
        },
        body: formData,
      });
      const data = await response.json();
      if (!response.ok) {
        setErrorCount(data.error_count);
        setErrorMessages(data.message);
        setFileValidated(false);
        setFileResults({});
      } else {
        setErrorCount(0);
        setErrorMessages([]);
        setFileValidated(true);
        setFileResults(data);
      }
      setBusyAction(null);
      setIsBusy(false);
    } catch (error) {
      setErrorCount(1);
      setErrorMessages(["Unknown error encountered"]);
      setFileValidated(false);
      setFileResults({});

      setBusyAction(null);
      setIsBusy(false);
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();

    if (!inputFile || isBusy) return;

    setIsBusy(true);
    setBusyAction("submit");

    const url = props.upload_url;
    const formData = new FormData();
    formData.append("file", inputFile);
    formData.append("manufacturer", event.target.manufacturer.value);
    formData.append("jira_ticket", jiraTicket);

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "X-CSRF-Token":
            document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") ?? "",
        },
        body: formData,
      });

      if (response.ok) {
        location.reload();
        return;
      }
    } catch (err) {}
    setBusyAction(null);
    setIsBusy(false);
  }

  function handleFileChange(event) {
    setErrorCount(0);
    setErrorMessages([]);
    setFileValidated(false);
    fileIsXLSX(event.target);
  }

  return (
    <React.Fragment>
      <Form onSubmit={handleSubmit} id="ndcUploadForm">
        <Form.Group className="mb-3" controlId="manufacturer">
          <Form.Label>
            Manufacturer <small className="text-danger">*</small>
          </Form.Label>
          <Form.Select
            required
            name="manufacturer"
            onChange={(e) => {
              setManufacturer(e.target.value);
            }}
          >
            <option value="">{"< Make a Selection >"}</option>
            {props.manufacturers.map((manufacturer) => (
              <option key={manufacturer.key} value={manufacturer.key}>
                {manufacturer.name}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
        <JiraTicket setJiraTicket={setJiraTicket} />
        <Form.Group
          controlId="headers"
          className="mb-3 p-3 bg-body-tertiary rounded border shadow-sm"
        >
          <Form.Label className="text-muted smaller">Required NDC File Headers:</Form.Label>
          <br />
          <b className="text-primary">
            NDC 11 (Item), Manufacturer Name, Product Name, Network, Pharmacy, Medical, Pharmacy
            Encounter, Medical Encounter, Lookback Days, Unit of Measure, HCPCS Code(s) Details
          </b>
          <br />
          <Form.Label className="text-muted smaller">Required NDC File Type:</Form.Label>
          <br />
          <b className="text-primary">.xlsx (Excel)</b>
          <br />
          <Form.Label className="text-muted smaller">File To Upload:</Form.Label>
          <p className="smaller">
            <span>
              Warning: This file is meant to be a complete list of NDCs for this manufacturer.{" "}
            </span>
            <span className="text-danger">
              Any NDC not in the file for this manufacturer will be <b>removed</b>.
            </span>
          </p>
          <Form.Control required name="file" type="file" onChange={handleFileChange} />
        </Form.Group>

        {errorMessages.length > 0 ? (
          <>
            <p className="text-danger">
              {errorCount} row(s) had errors (first 3 are shown):
              <br />
            </p>
            <ul className="text-danger" role="list">
              {renderErrors()}
            </ul>
          </>
        ) : null}
        {fileValidated ? (
          <div>
            File validated: {renderNdcStatusMessage()}
            {renderNcpdpHcpcsUnitConversionStatusMessage()}
          </div>
        ) : null}
        <div className="modal__button__container">
          <Button onClick={handleVerify} disabled={validateDisabled()}>
            {busyAction === "verify" ? (
              <>
                <Spinner
                  animation="border"
                  variant="light"
                  className="text-muted"
                  as="span"
                  size="sm"
                />
                Checking...
              </>
            ) : (
              "Check NDC File"
            )}
          </Button>

          <Button
            type="submit"
            variant="success"
            style={{ marginLeft: "8px" }}
            disabled={submitDisabled()}
          >
            {busyAction === "submit" ? (
              <>
                <Spinner
                  animation="border"
                  variant="light"
                  className="text-muted"
                  as="span"
                  size="sm"
                />
                Submitting...
              </>
            ) : (
              "Submit NDC File"
            )}
          </Button>
        </div>
      </Form>
    </React.Fragment>
  );
}
